import { VerticalSpacing } from "../VerticalSpacing";
import { V3CaptionGrey35 } from "../ReusableStyledComponents/V3CaptionGrey35";
import { V3IconUpload } from "../Icons";
import { V3Button } from "../Button";
import { V3ButtonColor } from "../Button/V3Button.types";
import { RightAlignPanel } from "../ReusableStyledComponents/RightAlignPanel";
import { AddContentUploadWrapper } from "./AddContentUpload.styles";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { useRef, useState } from "react";
import { FileUploadTarget } from "./AddContent/AddContent.types";
import toast from "react-hot-toast";
import { V3Loader } from "../Loader";

const getMediaDurationMinutes = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const media = document.createElement(file.type.startsWith("audio") ? "audio" : "video");

        media.src = url;
        media.onloadedmetadata = () => {
            resolve(media.duration / 60); // duration in minutes
            URL.revokeObjectURL(url);
        };
        media.onerror = () => {
            reject(new Error("Failed to load media"));
            URL.revokeObjectURL(url);
        };
    });
};

interface AddContentUploadProps {
    onBackHandler: () => void;
    onCloseHandler: () => void;
    onFilesChoosen: (files: File[]) => void;
    fileUploadTarget: FileUploadTarget;
}

export const AddContentUpload = ({ onBackHandler, onFilesChoosen, onCloseHandler, fileUploadTarget }: AddContentUploadProps) => {
    const inputFileRef = useRef<HTMLInputElement | null>(null);
    const [determingLength, setDeterminingLength] = useState(false);

    const isPDF = fileUploadTarget === FileUploadTarget.PDF;
    const isWebsite = fileUploadTarget === FileUploadTarget.Website;
    const isEmail = fileUploadTarget === FileUploadTarget.Email;
    const isWebsiteOrEmail = isWebsite || isEmail;
    const isCustomVideo = fileUploadTarget === FileUploadTarget.CustomVideo;
    const isCustomAudio = fileUploadTarget === FileUploadTarget.CustomAudio;
    let accept = isPDF ? ".pdf" : isWebsiteOrEmail ? ".png,.jpg" : isCustomVideo ? "video/*" : "audio/*";

    async function handleFileSelected(selectorFiles: FileList) {
        if (isCustomVideo || isCustomAudio) {
            const file = selectorFiles[0];
            if (!file) return;
            const duration = await getMediaDurationMinutes(file);

            const maxDuration = isCustomVideo ? 30 : 180;

            if (duration > maxDuration) {
                toast.error("Content length exceeds maximum allowed size");
                setDeterminingLength(false);
                return;
            }
        }

        onFilesChoosen(Array.from(selectorFiles));
        setDeterminingLength(false);
        if (inputFileRef && inputFileRef.current) inputFileRef.current.value = "";
    }

    return (
        <>
            <VerticalSpacing bottom={32}>
                <V3CaptionGrey35>
                    {isPDF && "Upload PDFs from your device below. You can upload multiple files at once. Maximum file size: 50MB"}
                    {isWebsiteOrEmail && (
                        <>
                            You can upload one or more screenshots of {isWebsite ? "a website" : "your emails"} below. If you select multiple files,
                            then they'll be displayed in the order that you've selected them. PNG and JPG are supported.
                        </>
                    )}
                    {isCustomVideo && <>Upload videos from your device below. Maximum video length: 30 minutes</>}
                    {isCustomAudio && <>Upload audio files from your device below. Maximum audio length: 180 minutes</>}
                </V3CaptionGrey35>
            </VerticalSpacing>
            {determingLength ? (
                <VerticalSpacing bottom={48} top={48}>
                    <V3Loader />
                </VerticalSpacing>
            ) : (
                <>
                    <VerticalSpacing bottom={48}>
                        <AddContentUploadWrapper onClick={() => inputFileRef && inputFileRef.current && inputFileRef.current.click()}>
                            <div>
                                <V3IconUpload />
                            </div>
                            <V3BodyGrey48>Select files</V3BodyGrey48>
                        </AddContentUploadWrapper>
                    </VerticalSpacing>
                    <input
                        style={{ display: "none" }}
                        type="file"
                        ref={inputFileRef}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => e.target.files && handleFileSelected(e.target.files)}
                        accept={accept}
                        multiple
                    />
                </>
            )}

            <RightAlignPanel>
                <div>
                    <V3Button text="Back" color={V3ButtonColor.secondary} onClick={onBackHandler} disabled={determingLength} />
                </div>
                <div>
                    <V3Button text="Close" autoWidth onClick={onCloseHandler} disabled={determingLength} />
                </div>
            </RightAlignPanel>
        </>
    );
};
